// Pure helpers for the Brands module. Every number/label here is derived
// client-side from the single query_output response (5729017198018560) per
// spec — the API sends no separate counter fields.

import {
  STATUS_COLORS,
  FALLBACK_COLOR,
} from "../../BrandConfigurationReport/BrandConfigurationReportChart";

export const BRAND_STATUS_LABELS: Record<string, string> = {
  ready: "Ready",
  not_ready: "Not Ready",
  config_issue: "Config Issue",
  account_issue: "Account Issue",
  not_connected: "Not Connected",
};

// User-friendly labels for the raw stuck_step values — used by both the
// "Stuck At" filter dropdown and the Needs Configuration row detail panel.
export const STUCK_AT_LABELS: Record<string, string> = {
  properties: "Properties",
  validations: "Validations",
  fetcher: "Fetcher",
  advisor: "Advisor",
  unifier: "Unifier",
};

export interface ReadyToRunStats {
  ready: number;
  total: number;
  percent: number;
}

// All Brands tab reference line: "X of Y brands are ready to run (Z%)"
export function computeReadyToRunStats(rows: any[] = []): ReadyToRunStats {
  const total = rows.length;
  const ready = rows.filter((row) => row?.status === "ready").length;
  const percent = total > 0 ? Math.round((ready / total) * 100) : 0;
  return { ready, total, percent };
}

// Needs Configuration tab count, used in the BrandsTabs label.
export function countByFlag(rows: any[] = [], flagKey: string): number {
  return rows.filter((row) => row?.[flagKey] === 1).length;
}

// Unique, sorted, non-empty values for a single-select filter dropdown
// (Status / Affiliate System / Company / Website / Stuck At / Reason).
export function getUniqueFieldOptions(rows: any[] = [], field: string): string[] {
  const values = new Set<string>();
  rows.forEach((row) => {
    const value = row?.[field];
    if (value !== null && value !== undefined && value !== "") {
      values.add(String(value));
    }
  });
  return Array.from(values).sort((a, b) => a.localeCompare(b));
}

// Long Website / Account values are clipped in the Brands tables so those
// columns keep a predictable width — the tail of a website or account name
// isn't needed to recognise which one it is. The Account cell pairs this with
// a tooltip carrying the untruncated value; Website deliberately doesn't,
// since the visible prefix is enough to identify the site.
export const WEBSITE_NAME_MAX_CHARS = 15;
export const ACCOUNT_NAME_MAX_CHARS = 20;

// Values at or under the limit are returned untouched, so short names never
// pick up a stray ellipsis.
export function truncateWithEllipsis(
  value: string | null | undefined,
  maxChars: number,
): string {
  const text = String(value ?? "");
  return text.length > maxChars ? `${text.slice(0, maxChars)}…` : text;
}

// "Brand Name (Brand ID)" label for a single row — same suggestion format
// as the legacy Brand Configuration Report's brand search. Used by the plain
// BrandNameSearch (Needs Configuration) so users can search by either name
// or id.
export function formatBrandSearchLabel(row: any): string {
  return `${row?.brand_name ?? ""} (${row?.brand_id ?? ""})`;
}

// Unique, sorted "Brand Name (Brand ID)" options for the BrandNameSearch
// suggestion list.
export function getBrandSearchOptions(rows: any[] = []): string[] {
  const seen = new Set<string>();
  const options: string[] = [];
  rows.forEach((row) => {
    if (!row?.brand_name) return;
    const label = formatBrandSearchLabel(row);
    if (!seen.has(label)) {
      seen.add(label);
      options.push(label);
    }
  });
  return options.sort((a, b) => a.localeCompare(b));
}

// Matches a row against a free-typed BrandNameSearch query by brand name OR
// brand id substring — checked against the same "Brand Name (Brand ID)"
// combined string the suggestion list shows, so typing either the name or
// the numeric id filters correctly.
export function matchesBrandNameOrId(row: any, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return formatBrandSearchLabel(row).toLowerCase().includes(q);
}

export interface DonutBucket {
  key: string;
  label: string;
  count: number;
  percent: string;
  color: string;
}

// Display config for known donut_bucket values, matching the legend labels
// of the legacy BrandConfigurationReportChart.tsx exactly (same display
// logic per spec). Colors are read directly from that module's STATUS_COLORS
// (single source of truth) rather than a duplicated copy, so the two charts
// can never drift apart. Any bucket value the backend sends that isn't
// listed here still renders — Title Cased, with the fallback color — instead
// of being silently dropped, since the frontend isn't meant to hardcode the
// bucket vocabulary.
const DONUT_BUCKET_CONFIG: Record<string, { label: string; color: string }> = {
  properties: { label: "Properties", color: STATUS_COLORS.Properties },
  validations: { label: "Stuck at Validations", color: STATUS_COLORS["Stuck at Validations"] },
  fetcher: { label: "Stuck at Fetcher", color: STATUS_COLORS["Stuck at Fetcher"] },
  advisor: { label: "Stuck at Advisor", color: STATUS_COLORS["Stuck at Advisor"] },
  unifier: { label: "Stuck at Unifier", color: STATUS_COLORS["Stuck at Unifier"] },
  running: { label: "Running", color: STATUS_COLORS.Running },
  pending: { label: "Pending", color: STATUS_COLORS.Pending },
};

const DONUT_FALLBACK_COLOR = FALLBACK_COLOR;

// Needs Configuration donut chart data — buckets rows by the already-computed
// `donut_bucket` field, per spec ("the frontend computes nothing"). Rows
// without a donut_bucket are excluded, matching the legacy chart's behavior
// of skipping rows it can't categorize.
export function computeDonutBuckets(rows: any[] = []): DonutBucket[] {
  const counts: Record<string, number> = {};
  rows.forEach((row) => {
    const bucket = row?.donut_bucket;
    if (!bucket) return;
    counts[bucket] = (counts[bucket] || 0) + 1;
  });

  const total = Object.values(counts).reduce((sum, n) => sum + n, 0);

  return Object.entries(counts)
    .map(([key, count]) => {
      // The API has been observed sending a "stuck_" prefixed variant (e.g.
      // "stuck_validations") for some buckets rather than the bare name
      // ("validations") — normalize before lookup so it still resolves to
      // the right label/color instead of falling back to the grey default.
      const config =
        DONUT_BUCKET_CONFIG[key] ??
        DONUT_BUCKET_CONFIG[key.replace(/^stuck_/, "")];
      return {
        key,
        label: config?.label ?? getColumnLabel(key),
        count,
        percent: total > 0 ? `${((count / total) * 100).toFixed(1)}%` : "0%",
        color: config?.color ?? DONUT_FALLBACK_COLOR,
      };
    })
    .sort((a, b) => b.count - a.count);
}

function getColumnLabel(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
